import { enqueueSnackbar } from 'notistack';
import React, { ReactNode, useCallback, useState } from 'react';

import {
  getActivityCalculatorSettingsByMerchantCodeApi,
  saveActivityCalculatorSettingsApi,
  updateActivityCalculatorSettingsApi,
  getActivityCalculatorSettingsByIdApi
} from 'src/api/activityCalculatorSettingsApi';
import { ActivityCalculatorSettings, ActivityCalculatorSettingsFull } from "../@types/activityCalculatorSettings";

//
//

export type ActivityCalculatorSettingsState = {
  listOfActivityCalcSettings: ActivityCalculatorSettings[];
  isLoadingListOfActivityCalcSettings: boolean;
  getActivityCalculatorSettingsList: (merchantCode: string) => void;
  saveActivityCalculatorSettings: (merchantCode: string, settings: ActivityCalculatorSettingsFull) => void;
  currentSettings: ActivityCalculatorSettingsFull | null;
  isLoadingCurrentSettings: boolean;
  getCurrentSettingsById: (id: string) => void;
  updateActivityCalculatorSettings: (id: string, settings: ActivityCalculatorSettingsFull) => void;
};

export const ActivityCalculatorSettingsContext = React.createContext<ActivityCalculatorSettingsState>({
  listOfActivityCalcSettings: [],
  isLoadingListOfActivityCalcSettings: true,
  getActivityCalculatorSettingsList: (merchantCode: string) => {},
  saveActivityCalculatorSettings: (merchantCode: string, settings: ActivityCalculatorSettingsFull) => {},
  currentSettings: null,
  isLoadingCurrentSettings: true,
  getCurrentSettingsById: (id: string) => {},
  updateActivityCalculatorSettings: (id: string, settings: ActivityCalculatorSettingsFull) => {},
});

type ActivityCalculatorSettingsProviderProps = {
  children: ReactNode;
};

export const ActivityCalculatorSettingsContextProvider = ({ children }: ActivityCalculatorSettingsProviderProps) => {
  const [listOfSettings, setListOfSettings] = useState<ActivityCalculatorSettings[] | []>([]);
  const [isLoadingListOfSettings, setIsLoadingListOfSettings] = useState<boolean>(false);
  const [currentSettings, setCurrentSettings] = useState<ActivityCalculatorSettingsFull | null>(null);
  const [isLoadingCurrentSettings, setIsLoadingCurrentSettings] = useState<boolean>(false);

  const getActivityCalculatorSettingsList = useCallback((merchantCode: string) => {
    setIsLoadingListOfSettings(true);
    getActivityCalculatorSettingsByMerchantCodeApi(merchantCode)
      .then((response: any) => {
        setListOfSettings(response.data);
      })
      .catch((response: any) => {
        console.error(response);
      })
      .finally(() => {
        setIsLoadingListOfSettings(false);
      })
  },[]);

  const saveActivityCalculatorSettings = useCallback((merchantCode: string, settings: ActivityCalculatorSettingsFull) => {
    setIsLoadingCurrentSettings(true);
    saveActivityCalculatorSettingsApi(merchantCode, settings)
      .then((response: any) => {
        enqueueSnackbar('Configuration is saved!', { variant: "success" });
        setCurrentSettings(response.data);
      })
      .catch((response: any) => {
        console.error(response);
      })
      .finally(() => {
        setIsLoadingCurrentSettings(false);
      })
  },[]);

  const getCurrentSettingsById = useCallback((id: string) => {
    setIsLoadingCurrentSettings(true);
    getActivityCalculatorSettingsByIdApi(id)
      .then((response: any) => {
        setCurrentSettings(response.data);
      })
      .catch((response: any) => {
        console.error(response);
      })
      .finally(() => {
        setIsLoadingCurrentSettings(false);
      })
  },[]);

  const updateActivityCalculatorSettings = useCallback((id: string, settings: ActivityCalculatorSettingsFull) => {
    setIsLoadingCurrentSettings(true);
    updateActivityCalculatorSettingsApi(id, settings)
      .then((response: any) => {
        setCurrentSettings({ ...response.data, id });
        enqueueSnackbar('Configuration is updated!', { variant: "success" });
      })
      .catch((response: any) => {
        console.error(response);
      })
      .finally(() => {
        setIsLoadingCurrentSettings(false);
      })
  },[]);

  return (
    <ActivityCalculatorSettingsContext.Provider
      value={{
        listOfActivityCalcSettings: listOfSettings,
        isLoadingListOfActivityCalcSettings: isLoadingListOfSettings,
        getActivityCalculatorSettingsList,
        saveActivityCalculatorSettings,
        isLoadingCurrentSettings,
        currentSettings,
        getCurrentSettingsById,
        updateActivityCalculatorSettings
      }}
    >
      {children}
    </ActivityCalculatorSettingsContext.Provider>
  );
};
